import { IComponentOptions, IOnChangesObjectOf, IPromise, IQService, IScope, ITimeoutService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { buildAssigneesSearchRequests, searchItemsToAssignees } from "@gtmhub/assignees/utils/search-utils";
import dayjs from "@gtmhub/dayjs";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalModalsService } from "@gtmhub/goals/services/goal-modals.service";
import { GoalsChangedEventArgs, OkrEvents, goalSelected, metricSelected } from "@gtmhub/okrs/events";
import { MetricService } from "@gtmhub/okrs/metrics/services/metric.service";
import { SearchService } from "@gtmhub/search/services/search.service";
import { TaskService } from "@gtmhub/tasks/services/task.service";
import { Assignee, AssigneeSelectorType } from "@webapp/assignees/models/assignee.models";
import { localize } from "@webapp/localization/utils/localization.utils";
import { Metric } from "@webapp/okrs/metrics/models/metric.models";
import { SearchOperatorsEnum } from "@webapp/search/models/search-api.models";
import { Search } from "@webapp/search/models/search.models";
import { ITaskParent, ITaskParentsIdMap, Task } from "@webapp/tasks/models/tasks.models";
import { UiToastService } from "@webapp/ui/toast/services/toast.service";
import { IGoal, SingleGoalSelectorInvokedBy } from "../../../goals/models";
import { GoalService } from "../../../goals/services/goal.service";
import { TaskEvents } from "../../task.events";

interface ITaskRowBindings {
  task: Task;
  openConfirmDelete(): void;
}

export interface ITaskRowCtrl extends IScope {
  task: Task;
  toggleDueDate: boolean;
  parentsIdMap: ITaskParentsIdMap;
  editedField: string;
  dueDate: string;
  confirmDelete: {
    on: boolean;
  };
  isTaskDeleted: boolean;
  editField(field: string): void;
  getAssignees(value: string);
  keyup(event, field: string): void;
  blur(field: string): void;
  selectNewAssignee(ids: string[]): void;
  focus(field: string): void;
  changeStatus(status: string): void;
  typeaheadBlur(): void;
  delete(): void;
  selectParent(): void;
  removeParent(): void;
  archive(): void;
  clone(taskForClone: Task): void;
  setDueDate(date: string): void;
  removeDueDate(): void;
  create(): IPromise<Task>;
}

export class TaskRowCtrl implements ITaskRowBindings {
  public task: Task;
  public openConfirmDelete: () => void;

  private taskOwnerType: AssigneeSelectorType = "user";
  private taskEvents: TaskEvents;
  private okrEvents: OkrEvents;

  public static $inject = [
    "$q",
    "$scope",
    "$state",
    "$timeout",
    "TaskService",
    "GoalService",
    "GoalModalsService",
    "UIErrorHandlingService",
    "SearchService",
    "MetricService",
    "UiToastService",
  ];

  constructor(
    private $q: IQService,
    private $scope: ITaskRowCtrl,
    private $state: StateService,
    private $timeout: ITimeoutService,
    private tasksService: TaskService,
    private goalService: GoalService,
    private goalModalsService: GoalModalsService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private searchService: SearchService,
    private metricService: MetricService,
    private toastService: UiToastService
  ) {
    this.taskEvents = new TaskEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
    this.okrEvents.onGoalsChanged((args) => this.onGoalsChanged(args));

    this.$scope.editField = this.editField;
    this.$scope.keyup = this.keyup;
    this.$scope.blur = this.blur;
    this.$scope.focus = this.focus;
    this.$scope.getAssignees = this.getAssignees;
    this.$scope.selectNewAssignee = this.selectNewAssignee;
    this.$scope.typeaheadBlur = this.typeaheadBlur;
    this.$scope.delete = this.delete;
    this.$scope.create = this.create;
    this.$scope.clone = this.clone;
    this.$scope.changeStatus = this.changeStatus;
    this.$scope.archive = this.archive;
    this.$scope.selectParent = this.selectParent;
    this.$scope.removeParent = this.removeParent;
    this.$scope.setDueDate = this.setDueDate;
    this.$scope.removeDueDate = this.removeDueDate;
    this.$scope.toggleDueDate = false;
    this.$scope.confirmDelete = {
      on: false,
    };
    this.$scope.isTaskDeleted = false;
  }

  public $onChanges(changes: IOnChangesObjectOf<ITaskRowBindings>): void {
    if (changes.task?.currentValue) {
      this.$scope.task = changes.task?.currentValue;
    }
  }

  private removeParent = (): void => {
    this.$scope.task.parentId = "";
    this.update("parentId");
  };

  private selectParent = (): void => {
    const parentType = this.$scope.task.parentType;
    const parentId = this.$scope.task.parentId;

    if (!parentType) {
      this.goalModalsService.openGoalSelectModal({ unselectableId: parentId, invokedById: this.$scope.task.id, invokedBy: "TaskRow" });
    } else if (parentType === "goal") {
      this.$state.go("._goalSidebar.goal", { id: parentId });
    } else if (parentType === "metric") {
      this.$state.go("._metric.metric", { metricId: parentId });
    }
  };

  private changeStatus = (status: string): void => {
    this.$scope.task.status = status;
    this.update("status");
  };

  private archive = (): void => {
    this.$scope.task.archived = true;
    this.update("archived");

    this.toastService.success(localize("task_archived"), {
      uiShouldTimeout: true,
      uiDuration: 5000,
      uiOffset: 40,
      uiPrimaryCTA: localize("undo"),
      uiOnPrimaryAction: () => {
        this.$scope.task.archived = false;
        this.update("archived");
      },
    });
  };

  private onGoalsChanged = (args: GoalsChangedEventArgs): void => {
    if (args.reason === metricSelected) {
      this.metricSelected(args.detail.metric, args.detail.invokedById, args.detail.invokedBy);
      return;
    }
    if (args.reason === goalSelected) {
      this.goalSelected(args.detail.goal, args.detail.invokedById, args.detail.invokedBy);
    }
  };

  private goalSelected = (selectedGoal: IGoal, invokedById: string, invokedBy: SingleGoalSelectorInvokedBy): void => {
    if (this.$scope.task.id === invokedById && invokedBy === "TaskRow") {
      this.$scope.task.parentId = selectedGoal.id;
      this.$scope.task.parentType = "goal";
      this.update("parentId");

      if (this.$scope.parentsIdMap) {
        const parent: ITaskParent = {
          parentId: selectedGoal.id,
          name: selectedGoal.name,
          parentType: "goal",
        };
        this.$scope.parentsIdMap[selectedGoal.id] = parent;
      }
    }
  };

  private metricSelected = (metric: Metric, invokedById: string, invokedBy: SingleGoalSelectorInvokedBy): void => {
    if (this.$scope.task.id === invokedById && invokedBy === "TaskRow") {
      this.$scope.task.parentId = metric.id;
      this.$scope.task.parentType = "metric";
      this.update("parentId");

      if (this.$scope.parentsIdMap) {
        const parent: ITaskParent = {
          parentId: metric.id,
          name: metric.name,
          parentType: "metric",
        };
        this.$scope.parentsIdMap[metric.id] = parent;
      }
    }
  };

  private delete = (): void => {
    this.tasksService.delete(this.$scope.task.id).then(
      () => {
        this.$scope.isTaskDeleted = true;
        if (this.$scope.task.parentType === "metric") {
          this.metricService.getMetric(this.$scope.task.parentId).then(
            (parentMetric: Metric) => {
              this.taskEvents.broadcastTaskDeleted({ task: this.$scope.task, sessionId: parentMetric.sessionId });
            },
            (error) => {
              this.$scope.confirmDelete.on = false;
              this.uiErrorHandlingService.handleModal(error);
            }
          );
        } else {
          // defaults to parent type 'goal'
          this.goalService.getGoal(this.$scope.task.parentId, { fields: "sessionId" }).then(
            (parentGoal: IGoal) => {
              this.taskEvents.broadcastTaskDeleted({ task: this.$scope.task, sessionId: parentGoal.sessionId });
            },
            (error) => {
              this.$scope.confirmDelete.on = false;
              this.uiErrorHandlingService.handleModal(error);
            }
          );
        }
      },
      (error) => {
        this.$scope.confirmDelete.on = false;
        this.uiErrorHandlingService.handleModal(error);
      }
    );
  };

  private create = (): IPromise<Task> => {
    const deferred = this.$q.defer<Task>();

    this.tasksService.create(this.$scope.task).then(
      (newTask) => {
        this.$scope.task = angular.copy(newTask);
        this.taskEvents.broadcastTaskCreated({ task: newTask, sessionId: this.$state.params.planningSessionId });
        return deferred.resolve(newTask);
      },
      (error) => {
        deferred.reject(error);
      }
    );

    return deferred.promise;
  };

  private clone = (taskForClone: Task): void => {
    const clonedTask = angular.copy(taskForClone);
    clonedTask.name = clonedTask.name + " Clone";
    this.tasksService.create(clonedTask, { gtmhubAdditionalParams: { cloned: true } }).then(
      (newTask) => {
        this.taskEvents.broadcastTaskCreated({ task: newTask, sessionId: this.$state.params.planningSessionId });

        this.focusTaskField(newTask.id, "name", 200);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private typeaheadBlur = (): void => {
    this.$timeout(() => {
      document.querySelector<HTMLInputElement>(`#task${this.$scope.task.id} [name='owner']`).value = "";
    });
  };

  private keyup = (event, field: string): void => {
    if (field !== "parentId") {
      if (event.key === "Enter") {
        if (!this.$scope.task.id) {
          this.create().then(
            () => {
              // prevents update of task on blur
              this.focus(field);

              // exit task name input and focus on task name of fake task to create a new task
              this.$timeout(() => document.querySelector<HTMLElement>(`#task${this.$scope.task.id} [ng-model='task.${field}']`).blur());
            },
            (error) => this.uiErrorHandlingService.handleModal(error)
          );
        } else {
          document.querySelector<HTMLElement>(`#task${this.$scope.task.id} [ng-model='task.${field}']`).blur();
          this.blur(field);
        }
      }

      if (event.key === "Tab") {
        const input = document.querySelector<HTMLInputElement>(`#task${this.$scope.task.id} [ng-model='task.${field}']`);
        input.selectionStart = input.selectionEnd;
      }
    }
  };

  private setDueDate = (date: string): void => {
    date = dayjs(date).format();

    this.$scope.dueDate = date;
    this.$scope.task.dueDate = date;

    if (!this.$scope.task.id) {
      this.create();
    } else {
      this.update("dueDate");
    }

    this.$scope.toggleDueDate = false;
  };

  private removeDueDate = (): void => {
    this.$scope.task.unset = {
      dueDate: true,
    };
    this.$scope.dueDate = "";
    this.$scope.task.dueDate = "";

    this.update("unset");

    this.$scope.toggleDueDate = false;
  };

  private selectNewAssignee = (ids: string[]): void => {
    const newOwnerId = ids[0];
    const oldTaskOwnerId = this.$scope.task.ownerId;

    /** if owner has not changed, no need to reassign */
    if (newOwnerId === oldTaskOwnerId) {
      return;
    }
    // idea is to change owner immediately not wait for response
    this.$scope.task.ownerId = newOwnerId;
    const update = { ownerId: newOwnerId };

    if (!this.$scope.task.id) {
      this.create();
    } else {
      this.tasksService.update(this.$scope.task.id, update).catch((error) => {
        this.$scope.task.ownerId = oldTaskOwnerId;
        this.uiErrorHandlingService.handleModal(error);
      });
    }
  };

  private getAssignees = (searchTerm: string): IPromise<Assignee[]> => {
    const searchBody = {
      searchTerm,
      operator: searchTerm ? SearchOperatorsEnum.matchPrefix : SearchOperatorsEnum.matchAny,
      searchRequests: buildAssigneesSearchRequests(this.taskOwnerType, searchTerm),
    };

    return this.searchService.getSomeSearchData<Search<"teams"> | Search<"users">>(searchBody).then(
      (response) => searchItemsToAssignees(response.items),
      () => []
    );
  };

  private update = (field: string): IPromise<unknown> => {
    const update: Partial<Task> = { [field]: this.$scope.task[field] };

    if (field === "parentId" && this.$scope.task.parentType === "metric") {
      update.parentType = "metric";
    }

    return this.tasksService.update(this.$scope.task.id, update).then(
      (updatedTask) => {
        this.taskEvents.broadcastTaskUpdated({ task: updatedTask, update });
      },
      (error) => this.uiErrorHandlingService.handleModal(error, {})
    );
  };

  private blur = (field: string): void => {
    const update = { [field]: this.$scope.task[field] };

    if (field === "name" && this.$scope.task.name === "") {
      this.$scope.task.name = this.$scope.task["name-original"] || localize("new_task");
      return;
    }

    if (this.$scope.task.id && this.$scope.task[field + "-original"] !== this.$scope.task[field]) {
      this.tasksService.update(this.$scope.task.id, update).catch((error) => this.uiErrorHandlingService.handleModal(error, {}));
    } else if (!this.$scope.task.id && this.$scope.task[field] !== "") {
      this.create();
    }
  };

  private focus = (field: string): void => {
    this.$scope.task[field + "-original"] = this.$scope.task[field];
  };

  private editField = (field: string): void => {
    this.$scope.editedField = field;
    this.focusTaskField(this.$scope.task.id, field);
  };

  private focusTaskField = (taskId: string, field: string, delay?: number) => {
    this.$timeout(() => {
      const el = document.querySelector<HTMLElement>(`#task${taskId} [ng-model='task.${field}']`);
      if (el) {
        el.focus();
      }
    }, delay);
  };
}

export const TaskRowComponent: IComponentOptions = {
  template: require("./task-row.html"),
  controller: TaskRowCtrl,
  bindings: {
    task: "<?",
    openConfirmDelete: "&?",
  },
};
